/**
 * Synthetic search filters — UI-only search keys that the API does not know about.
 *
 * The search bar (MacroInput) normally sends what the user types to the backend as-is
 * (`status:active` -> `{ field: 'status', ... }`). A synthetic filter is a friendlier key
 * that exists only in the UI and is translated to the real API filter right before the
 * search request is sent:
 *
 *   archived:true   -> { field: 'archival_status', operator: 'eq',     value: 'ARCHIVED' }
 *   archived:false  -> ['or', { field: 'archival_status', operator: 'eq', value: 'LIVE' },
 *                             { field: 'archival_status', operator: 'eq', value: 'RESTORING' }]
 *   is_valid:true   -> { field: 'verified_status', operator: 'contains',     value: '200' }
 *   is_valid:false  -> { field: 'verified_status', operator: 'not_contains', value: '200' }
 *
 * Each entry below is the single definition of its key: the dropdown label/values, the
 * views it appears in, and its API translation. visibilityFilters.ts (keys, value types,
 * per-view whitelists), visibilityFiltersReducer.js (dropdown) and
 * SearchFilterUtils.processSearchFilters (translation) all read from this registry.
 *
 * To add one: add an entry here. Nothing else needs to change.
 *
 * `toApiFilter` returns null for input it doesn't understand (unknown value or operator).
 * The filter is then sent untranslated, so the API rejects the unknown field and the
 * search bar marks it invalid — the UI never guesses (and possibly inverts) intent.
 *
 * Only `import type` from js/constants/visibilityFilters: that module imports this one,
 * so a runtime import back would be circular.
 */
import {
  ARCHIVAL_STATUS_ARCHIVED,
  ARCHIVAL_STATUS_LIVE,
  ARCHIVAL_STATUS_RESTORING,
} from 'js/constants/pipelineConstants';
import type { SearchFilterViewsType } from 'js/constants/visibilityFilters';

type SearchFilterOpType =
  | 'eq'
  | 'not_eq'
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'ends_with'
  | 'gt'
  | 'lt'
  | 'empty'
  | 'not_empty';

export interface SearchFilterMacro {
  field: string;
  operator: string;
  value: unknown;
}

export interface ApiSearchFilter {
  field: string;
  operator: SearchFilterOpType;
  value: string | number | boolean;
}

/** A filter, or an ['and' | 'or', ...filters] group of them, as the search API accepts */
export type ApiSearchFilterTree = ApiSearchFilter | ['and' | 'or', ...ApiSearchFilterTree[]];

interface SyntheticSearchFilterValue {
  label: string;
  value: string;
  // false = never match this value from free text (see archived "Not archived")
  matchFreeText?: boolean;
}

export interface SyntheticSearchFilter<K extends string = string, T extends string = string> {
  /** Constant name in SearchFilterTypes, e.g. SearchFilterTypes.ARCHIVED */
  key: K;
  /** The key users type, e.g. `archived` in `archived:true` */
  type: T;
  label: string;
  description: string;
  views: SearchFilterViewsType[];
  availableOpTypes: SearchFilterOpType[];
  availableValues: SyntheticSearchFilterValue[];
  toApiFilter: (macro: { operator: string; value: unknown }) => ApiSearchFilterTree | null;
}

const FLOW_VIEWS: SearchFilterViewsType[] = [
  'PIPELINES',
  'PROJECT_PIPELINES',
  'SEARCH_PIPELINES',
  'MCP_FLOWS',
];

// Identity helper that keeps `key`/`type` as string literals, so SearchFilterTypes stays
// exactly typed (a typo like SearchFilterTypes.ARCHIVD is still a compile error).
const defineSyntheticFilter = <K extends string, T extends string>(
  filter: SyntheticSearchFilter<K, T>,
) => filter;

export const SYNTHETIC_SEARCH_FILTERS = [
  defineSyntheticFilter({
    key: 'IS_VALID',
    type: 'is_valid',
    label: 'Credential Validity',
    description: 'Whether the credential is valid or not',
    views: ['CREDENTIALS'],
    availableOpTypes: ['eq', 'not_eq'],
    availableValues: [
      { label: 'Valid', value: 'true' },
      { label: 'Invalid', value: 'false' },
    ],
    // Pre-registry behavior, kept as-is: exact 'true'/'false' only; the operator is ignored.
    toApiFilter: ({ value }) => {
      if (value === 'true') {
        return { field: 'verified_status', operator: 'contains', value: '200' };
      }
      if (value === 'false') {
        return { field: 'verified_status', operator: 'not_contains', value: '200' };
      }
      return null;
    },
  }),
  defineSyntheticFilter({
    key: 'ARCHIVED',
    type: 'archived',
    // Reads as a sentence in the suggestion row: "Flow is Archived" / "Flow is Not archived"
    label: 'Flow',
    description: 'Whether the flow is archived',
    views: FLOW_VIEWS,
    availableOpTypes: ['eq', 'not_eq'],
    availableValues: [
      { label: 'Archived', value: 'true' },
      // free text like "archived" would otherwise also expand to "not archived" and match nearly
      // every flow
      { label: 'Not archived', value: 'false', matchFreeText: false },
    ],
    // Only fully ARCHIVED flows count as archived. RESTORING flows are on their way back to LIVE,
    // so archived:false intentionally includes them.
    // "Not archived" is sent as an eq match on the other statuses, never not_eq ARCHIVED: flow search
    // also matches sources/datasets/sinks/credentials, which have no archival_status, and on Rails
    // a not_eq matches those, bringing their archived flows back. eq only matches flows on both
    // Rails and Python. Keep the list in sync with ARCHIVAL_STATUS_* in pipelineConstants.
    // Flow search rejects is_origin with a 400, so this must stay archival_status filters only.
    toApiFilter: ({ operator, value }) => {
      const normalizedValue = String(value).toLowerCase();

      if (operator !== 'eq' && operator !== 'not_eq') {
        return null;
      }
      if (normalizedValue !== 'true' && normalizedValue !== 'false') {
        return null;
      }

      const wantsArchived = (normalizedValue === 'true') !== (operator === 'not_eq');

      if (wantsArchived) {
        return { field: 'archival_status', operator: 'eq', value: ARCHIVAL_STATUS_ARCHIVED };
      }

      return [
        'or',
        { field: 'archival_status', operator: 'eq', value: ARCHIVAL_STATUS_LIVE },
        { field: 'archival_status', operator: 'eq', value: ARCHIVAL_STATUS_RESTORING },
      ];
    },
  }),
] as const;

type RegisteredSyntheticFilter = (typeof SYNTHETIC_SEARCH_FILTERS)[number];

const SYNTHETIC_FILTERS_BY_TYPE: Record<string, SyntheticSearchFilter | undefined> =
  Object.fromEntries(SYNTHETIC_SEARCH_FILTERS.map((filter) => [filter.type, filter]));

/** For SearchFilterTypes: exactly { IS_VALID: 'is_valid'; ARCHIVED: 'archived' } */
export const SyntheticSearchFilterTypes = Object.fromEntries(
  SYNTHETIC_SEARCH_FILTERS.map(({ key, type }) => [key, type]),
) as { [F in RegisteredSyntheticFilter as F['key']]: F['type'] };

/** For SearchFilterValueTypes: every synthetic filter has fixed values, so 'enum'. */
export const SyntheticSearchFilterValueTypes: Record<string, 'enum'> = Object.fromEntries(
  SYNTHETIC_SEARCH_FILTERS.map(({ type }) => [type, 'enum' as const]),
);

/** For the visibilityFilters reducer's availableFilters (search bar dropdown). */
export const syntheticAvailableFilters = Object.fromEntries(
  SYNTHETIC_SEARCH_FILTERS.map(
    ({ type, label, description, availableOpTypes, availableValues }) => [
      type,
      { type, label, description, availableOpTypes, availableValues },
    ],
  ),
);

/** Appends each view's synthetic filter types to its whitelist (AvailableFilterTypesByView). */
export function withSyntheticFilterTypes<T extends Record<string, string[]>>(byView: T): T {
  return Object.fromEntries(
    Object.entries(byView).map(([view, types]) => [
      view,
      [
        ...types,
        ...SYNTHETIC_SEARCH_FILTERS.filter(({ views }) =>
          views.includes(view as SearchFilterViewsType),
        ).map(({ type }) => type),
      ],
    ]),
  ) as T;
}

/**
 * Drops synthetic macros whose value can't be translated yet (e.g. `archived:fa` while the user
 * is still typing), for the search-as-you-type suggestion requests only. Sent untranslated,
 * they would 400, and the search bar would mark the key itself invalid for the rest of the
 * session. A submitted search still sends them, so a real typo is still flagged.
 */
export function dropIncompleteSyntheticMacros<S>(segments: S[]): S[] {
  return segments.filter((segment) => {
    if (typeof segment !== 'object' || segment === null || !('token' in segment)) {
      return true;
    }

    const { token, operator, value } = segment as {
      token: string;
      operator?: string;
      value?: unknown;
    };
    const synthetic = SYNTHETIC_FILTERS_BY_TYPE[token];
    const trimmedValue = typeof value === 'string' ? value.trim() : value;

    // no value: processSearchFilters drops the macro anyway
    if (!synthetic || !trimmedValue) {
      return true;
    }

    return (
      synthetic.toApiFilter({
        operator: operator || synthetic.availableOpTypes[0],
        value: trimmedValue,
      }) !== null
    );
  });
}

const isMacro = (filter: unknown): filter is SearchFilterMacro =>
  typeof filter === 'object' && filter !== null && !Array.isArray(filter) && 'field' in filter;

/**
 * Translates synthetic filters in a processed filter tree (['and' | 'or', ...filters]) into
 * the API filters they stand for. Everything else is returned unchanged. Pure: never
 * mutates its input.
 */
export function applySyntheticSearchFilters(filters: unknown[]): unknown[] {
  return filters.map((filter) => {
    if (Array.isArray(filter)) {
      return applySyntheticSearchFilters(filter);
    }

    if (!isMacro(filter)) {
      return filter;
    }

    const synthetic = SYNTHETIC_FILTERS_BY_TYPE[filter.field];
    return synthetic?.toApiFilter(filter) ?? filter;
  });
}
